/* eslint-disable prettier/prettier */
import { useState, useCallback, useMemo } from 'react';
import {
	CalendarViewMode,
	MonthRange,
	ScheduleViewMode,
	SchedulerState,
	WeekRange,
} from '../types/schedule.types';
import {
	addMonths,
	addWeeks,
	getMonthRange,
	getMonthRangeFor,
	getWeekRange,
} from '../utils/dateUtils';

function makeInitialState(reference: Date = new Date()): SchedulerState {
	const ref = new Date(
		reference.getFullYear(),
		reference.getMonth(),
		reference.getDate()
	);
	return {
		viewMode: 'week',
		selectedWeek: getWeekRange(ref),
		selectedMonth: getMonthRangeFor(ref),
		currentMonth: ref.getMonth(),
		currentYear: ref.getFullYear(),
		isCalendarOpen: false,
		calendarView: 'week',
	};
}

export function useScheduler() {
	const [state, setState] = useState<SchedulerState>(() =>
		makeInitialState()
	);

	const setViewMode = useCallback((viewMode: ScheduleViewMode) => {
		setState(s => {
			if (s.viewMode === viewMode) return s;
			// Toggling resets to the CURRENT period (today's week / month).
			const today = new Date();
			const ref = new Date(
				today.getFullYear(),
				today.getMonth(),
				today.getDate()
			);
			const selectedWeek = getWeekRange(ref);
			const selectedMonth = getMonthRangeFor(ref);
			return {
				...s,
				viewMode,
				selectedWeek,
				selectedMonth,
				currentMonth:
					viewMode === 'month'
						? selectedMonth.month
						: selectedWeek.start.getMonth(),
				currentYear:
					viewMode === 'month'
						? selectedMonth.year
						: selectedWeek.start.getFullYear(),
				isCalendarOpen: false,
				calendarView: 'week',
			};
		});
	}, []);

	const openCalendar = useCallback(() => {
		setState(s => ({
			...s,
			isCalendarOpen: true,
			// In month mode the popup opens straight on the month picker.
			calendarView: s.viewMode === 'month' ? 'month' : 'week',
			currentMonth:
				s.viewMode === 'month'
					? s.selectedMonth.month
					: s.selectedWeek.start.getMonth(),
			currentYear:
				s.viewMode === 'month'
					? s.selectedMonth.year
					: s.selectedWeek.start.getFullYear(),
		}));
	}, []);

	const closeCalendar = useCallback(() => {
		setState(s => ({ ...s, isCalendarOpen: false }));
	}, []);

	const selectWeek = useCallback((week: WeekRange) => {
		setState(s => ({
			...s,
			selectedWeek: week,
			selectedMonth: getMonthRangeFor(week.start),
			currentMonth: week.start.getMonth(),
			currentYear: week.start.getFullYear(),
			isCalendarOpen: false,
		}));
	}, []);

	const selectMonthRange = useCallback((month: MonthRange) => {
		setState(s => ({
			...s,
			selectedMonth: month,
			// Keep week selection consistent: first week of the month.
			selectedWeek: getWeekRange(new Date(month.year, month.month, 1)),
			currentMonth: month.month,
			currentYear: month.year,
			isCalendarOpen: false,
		}));
	}, []);

	const navigatePrev = useCallback(() => {
		setState(s =>
			s.viewMode === 'week'
				? withWeek(s, addWeeks(s.selectedWeek, -1))
				: withMonth(s, addMonths(s.selectedMonth, -1))
		);
	}, []);

	const navigateNext = useCallback(() => {
		setState(s =>
			s.viewMode === 'week'
				? withWeek(s, addWeeks(s.selectedWeek, 1))
				: withMonth(s, addMonths(s.selectedMonth, 1))
		);
	}, []);

	const setCalendarView = useCallback((view: CalendarViewMode) => {
		setState(s => ({ ...s, calendarView: view }));
	}, []);

	const navigateMonth = useCallback((direction: 'prev' | 'next') => {
		setState(s => {
			const moved = getMonthRange(
				s.currentYear,
				s.currentMonth + (direction === 'prev' ? -1 : 1)
			);
			return {
				...s,
				currentMonth: moved.month,
				currentYear: moved.year,
			};
		});
	}, []);

	const selectMonth = useCallback((month: number) => {
		setState(s => {
			if (s.viewMode === 'month') {
				// Month mode: picking a month IS the selection.
				const range = getMonthRange(s.currentYear, month);
				return {
					...s,
					selectedMonth: range,
					selectedWeek: getWeekRange(
						new Date(range.year, range.month, 1)
					),
					currentMonth: range.month,
					currentYear: range.year,
					isCalendarOpen: false,
				};
			}
			// Week mode: just page the mini-calendar to that month.
			return { ...s, currentMonth: month, calendarView: 'week' };
		});
	}, []);

	const selectYear = useCallback((year: number) => {
		setState(s => ({
			...s,
			currentYear: year,
			calendarView: s.viewMode === 'month' ? 'month' : 'week',
		}));
	}, []);

	const activeRange: WeekRange = useMemo(
		() =>
			state.viewMode === 'week'
				? state.selectedWeek
				: state.selectedMonth,
		[state.viewMode, state.selectedWeek, state.selectedMonth]
	);

	return {
		state,
		activeRange,
		setViewMode,
		openCalendar,
		closeCalendar,
		selectWeek,
		selectMonthRange,
		navigatePrev,
		navigateNext,
		setCalendarView,
		navigateMonth,
		selectMonth,
		selectYear,
	};
}

function withWeek(s: SchedulerState, week: WeekRange): SchedulerState {
	return {
		...s,
		selectedWeek: week,
		selectedMonth: getMonthRangeFor(week.start),
		currentMonth: week.start.getMonth(),
		currentYear: week.start.getFullYear(),
	};
}

function withMonth(s: SchedulerState, month: MonthRange): SchedulerState {
	return {
		...s,
		selectedMonth: month,
		selectedWeek: getWeekRange(new Date(month.year, month.month, 1)),
		currentMonth: month.month,
		currentYear: month.year,
	};
}
